import React, { useState, useEffect, useCallback } from "react";
import {
  User,
  Mail,
  Phone,
  Hash,
  IdCard,
  BookOpen,
  FileText,
  Trophy,
  Award,
  TrendingUp,
  Target,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  Calendar,
  BarChart3,
  ClipboardCheck,
  Loader2,
  RefreshCw,
  GraduationCap,
  UserCheck,
  ShoppingCart,
} from "lucide-react";
import { StudentReportData, StudentReportType, PaginationMeta, StudentTest } from "../../../../types/student-report";
import { ApiService, MY_REPORT } from "../../../../services/api";
import Modal from "../../../../components/ui/Modal";
import StudentPracticeExamsSection from "./StudentPracticeExamsSection";

interface StudentReportDisplayProps {
  userId: string;
  reportType: StudentReportType;
  selectedMainLessonFilter?: string | null;
  selectedTopicFilter?: string | null;
}

interface StudentSummaryData {
  student: StudentReportData["student"];
  summary: StudentReportData["summary"];
  lessonStatistics: StudentReportData["lessonStatistics"];
  topicStatistics: StudentReportData["topicStatistics"];
  reportType: StudentReportType;
}

interface StudentTestsData {
  reportType: StudentReportType;
  tests: {
    meta: PaginationMeta;
    data: StudentTest[];
  };
}

export default function StudentReportDisplay({
  userId,
  reportType,
  selectedMainLessonFilter,
  selectedTopicFilter,
}: StudentReportDisplayProps) {
  const [summaryData, setSummaryData] = useState<StudentSummaryData | null>(null);
  const [testsData, setTestsData] = useState<StudentTestsData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [testsLoading, setTestsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedLessonFilter, setSelectedLessonFilter] = useState<string | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ url: string; name: string } | null>(null);
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<string>("DESC");
  const testsPerPage = 10;

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return phone;
    if (phone.startsWith("0")) return phone;
    return `0${phone}`;
  };

  const loadSummaryData = useCallback(async () => {
    if (!userId) return;

    try {
      setSummaryLoading(true);
      setError(null);
      setSummaryData(null);

      const filters = {
        lessonId: selectedMainLessonFilter || undefined,
        topicId: selectedTopicFilter || undefined,
        type: reportType,
      };

      try {
        // Try the new summary endpoint first
        const response = await ApiService.getStudentReportSummary(userId, filters);
        setSummaryData(response);
      } catch (summaryError) {
        // Raporlarım'da eski uç yok (/report/student/:id öğrenciye kapalı): asıl hatayı göster.
        if (userId === MY_REPORT) throw summaryError;
        console.warn("New summary endpoint failed, falling back to old endpoint:", summaryError);
        // Fallback to old endpoint
        const response = await ApiService.getStudentReport(userId, filters);
        if (response) {
          setSummaryData({
            student: response.student,
            summary: response.summary || response.testReports?.summary,
            lessonStatistics: response.lessonStatistics || response.testReports?.lessonStatistics || [],
            topicStatistics: response.topicStatistics || response.testReports?.topicStatistics || [],
            reportType: response.reportType || reportType,
          });
        }
      }
    } catch (err: any) {
      console.error("Error loading student summary:", err);
      setError(err.message || "Özet veriler yüklenirken hata oluştu");
    } finally {
      setSummaryLoading(false);
    }
  }, [userId, reportType, selectedMainLessonFilter, selectedTopicFilter]);

  const loadTestsData = useCallback(async () => {
    if (!userId) return;

    try {
      setTestsLoading(true);
      setError(null);
      setTestsData(null);

      const filters = {
        lessonId: selectedMainLessonFilter || undefined,
        topicId: selectedTopicFilter || undefined,
        type: reportType,
        sortBy,
        sortOrder,
        page: currentPage,
        limit: testsPerPage,
      };

      try {
        // Try the new tests endpoint first
        const response = await ApiService.getStudentTestsList(userId, filters);
        setTestsData(response);
      } catch (testsError) {
        if (userId === MY_REPORT) throw testsError;
        console.warn("New tests endpoint failed, falling back to old endpoint:", testsError);
        // Fallback to old endpoint
        const response = await ApiService.getStudentReport(userId, filters);
        if (response) {
          const actualTests = response.tests?.data || response.testReports?.tests || [];

          // Apply client-side pagination as fallback
          const startIndex = (currentPage - 1) * testsPerPage;
          const endIndex = startIndex + testsPerPage;
          const paginatedTests = actualTests.slice(startIndex, endIndex);

          setTestsData({
            reportType: response.reportType || reportType,
            tests: {
              meta: {
                page: currentPage,
                limit: testsPerPage,
                totalItems: actualTests.length,
                totalPages: Math.ceil(actualTests.length / testsPerPage),
                hasNextPage: endIndex < actualTests.length,
                hasPrevPage: currentPage > 1,
              },
              data: paginatedTests,
            },
          });
        }
      }
    } catch (err: any) {
      console.error("Error loading student tests:", err);
      setError(err.message || "Test verileri yüklenirken hata oluştu");
    } finally {
      setTestsLoading(false);
    }
  }, [userId, reportType, selectedMainLessonFilter, selectedTopicFilter, sortBy, sortOrder, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    setSummaryData(null);
    setTestsData(null);
    setError(null);
  }, [reportType, selectedMainLessonFilter, selectedTopicFilter]);

  useEffect(() => {
    loadSummaryData();
  }, [loadSummaryData]);

  useEffect(() => {
    loadTestsData();
  }, [loadTestsData]);

  const getPerformanceLevel = (score: number) => {
    if (score >= 90)
      return {
        text: "Mükemmel",
        shortText: "Mükemmel",
        icon: Trophy,
        color: "text-yellow-600",
        bgColor: "bg-yellow-100",
      };
    if (score >= 80)
      return {
        text: "Çok İyi",
        shortText: "Çok İyi",
        icon: Award,
        color: "text-green-600",
        bgColor: "bg-green-100",
      };
    if (score >= 70)
      return {
        text: "İyi",
        shortText: "İyi",
        icon: TrendingUp,
        color: "text-blue-600",
        bgColor: "bg-blue-100",
      };
    if (score >= 50)
      return {
        text: "Orta",
        shortText: "Orta",
        icon: Target,
        color: "text-orange-600",
        bgColor: "bg-orange-100",
      };
    return {
      text: "Geliştirilmeli",
      shortText: "Geliştirilmeli",
      icon: AlertCircle,
      color: "text-red-600",
      bgColor: "bg-red-100",
    };
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Başarılı
          </span>
        );
      case "FAILED":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Başarısız
          </span>
        );
      case "PENDING":
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            Bekleyen
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <AlertCircle className="w-3 h-3 mr-1" />
            Bilinmiyor
          </span>
        );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleSortChange = (newSortBy: string) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === "ASC" ? "DESC" : "ASC");
    } else {
      setSortBy(newSortBy);
      setSortOrder("DESC");
    }
    setCurrentPage(1);
  };

  // Deneme bölümü özetten bağımsızdır: özet yavaşken/boşken de görünür; her durumda 2. çocuk ve aynı anahtarla
  // durduğu için özet gelince yeniden yüklenmez, açık karne kapanmaz.
  const practiceExamsSection = <StudentPracticeExamsSection key="practice-exams" userId={userId} />;

  if (error) {
    return (
      <div className="w-full space-y-4 sm:space-y-6 pb-4 sm:pb-6 px-2 sm:px-4 lg:px-0">
      <div key="report-state" className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-16 h-16 text-red-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Hata Oluştu</h3>
        <p className="text-gray-500 max-w-md mb-4">{error}</p>
        <button
          onClick={() => {
            setError(null);
            loadSummaryData();
            loadTestsData();
          }}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          <RefreshCw className="w-4 h-4 mr-2" />
          Tekrar Dene
        </button>
      </div>
      {practiceExamsSection}
      </div>
    );
  }

  if (summaryLoading && !summaryData) {
    return (
      <div className="w-full space-y-4 sm:space-y-6 pb-4 sm:pb-6 px-2 sm:px-4 lg:px-0">
      <div key="report-state" className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Yükleniyor...</span>
      </div>
      {practiceExamsSection}
      </div>
    );
  }

  if (!summaryData) {
    return (
      <div className="w-full space-y-4 sm:space-y-6 pb-4 sm:pb-6 px-2 sm:px-4 lg:px-0">
      <div key="report-state" className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-16 h-16 text-yellow-300 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Veri Bulunamadı</h3>
        <p className="text-gray-500 max-w-md">{userId === MY_REPORT ? "Henüz raporlanacak bir çalışman yok. Test çözdükçe burada görünür." : "Bu öğrenci için rapor verisi bulunamadı."}</p>
      </div>
      {practiceExamsSection}
      </div>
    );
  }

  const { student, summary, lessonStatistics = [], topicStatistics = [] } = summaryData;
  const filteredTopicStatistics = selectedLessonFilter
    ? topicStatistics.filter((topic) => topic.lessonId === selectedLessonFilter)
    : topicStatistics;

  const availableLessons = lessonStatistics.map((lesson) => ({
    id: lesson.lessonId,
    title: lesson.lessonTitle,
  }));

  const overallPerformance = getPerformanceLevel(summary?.overallSuccessPercent || 0);
  const OverallIcon = overallPerformance.icon;

  const hasParentInfo = student.metadata?.parentPhone || student.metadata?.parentEmail;
  const hasOrderInfo = student.metadata?.orderCode || student.metadata?.orderDate;
  const hasParentOrOrderInfo = hasParentInfo || hasOrderInfo;

  return (
    <div className="w-full space-y-4 sm:space-y-6 pb-4 sm:pb-6 px-2 sm:px-4 lg:px-0">
      <div
        key="report-state"
        className={`grid grid-cols-1 md:grid-cols-2 ${
          hasParentOrOrderInfo ? "lg:grid-cols-4" : "lg:grid-cols-3"
        } gap-4 sm:gap-6 items-stretch`}>
        <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 h-full">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
            <User className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Öğrenci Bilgileri
          </h3>

          <div className="flex flex-col sm:flex-row gap-4">
            <div
              className={`w-20 h-20 sm:w-24 sm:h-24 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden mx-auto sm:mx-0 ${
                student.profilePhoto?.url ? "cursor-pointer hover:opacity-80 transition-opacity" : ""
              }`}
              onClick={(e) => {
                if (student.profilePhoto?.url) {
                  e.stopPropagation();
                  setSelectedImage({ url: student.profilePhoto.url, name: student.fullName });
                  setIsImageModalOpen(true);
                }
              }}>
              {student.profilePhoto?.url ? (
                <img src={student.profilePhoto.url} alt={student.fullName} className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-bold text-xl sm:text-2xl">
                  {student.firstName?.charAt(0)}
                  {student.lastName?.charAt(0)}
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0 space-y-3">
              <div className="pb-3 border-b border-gray-200 text-center sm:text-left">
                <div className="text-lg sm:text-xl font-semibold text-gray-900 break-words">{student.fullName}</div>
                <div className="text-sm text-gray-600 font-medium">{student.department?.name || "Öğrenci"}</div>
              </div>

              <div className="space-y-3">
                {student.email && (
                  <div>
                    <div className="flex items-center mb-1">
                      <Mail className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-500 font-medium">E-posta:</span>
                    </div>
                    <div className="text-sm text-gray-900 break-all pl-6">{student.email}</div>
                  </div>
                )}
                {(student.schoolNumber || student.metadata?.schoolNumber) && (
                  <div>
                    <div className="flex items-center mb-1">
                      <GraduationCap className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-500 font-medium">Okul Numarası:</span>
                    </div>
                    <div className="text-sm text-gray-900 break-all pl-6">{student.schoolNumber || student.metadata?.schoolNumber}</div>
                  </div>
                )}
                {(student.tcNumber || student.metadata?.tcNumber) && (
                  <div>
                    <div className="flex items-center mb-1">
                      <IdCard className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-500 font-medium">TC Kimlik No:</span>
                    </div>
                    <div className="text-sm text-gray-900 break-all pl-6">{student.tcNumber || student.metadata?.tcNumber}</div>
                  </div>
                )}
                {student.phone && (
                  <div>
                    <div className="flex items-center mb-1">
                      <Phone className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-500 font-medium">Telefon:</span>
                    </div>
                    <div className="text-sm text-gray-900 break-all pl-6">{formatPhoneNumber(student.phone)}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Veli ve Sipariş Bilgileri Kutusu */}
        {hasParentOrOrderInfo && (
          <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 h-full">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
              <UserCheck className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Veli & Sipariş Bilgileri
            </h3>

            <div className="space-y-4">
              {/* Veli Bilgileri */}
              <div className="text-center space-y-2">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Veli Bilgileri</h4>
                {hasParentInfo ? (
                  <>
                    {student.metadata?.parentPhone ? (
                      <div className="flex items-center justify-center">
                        <Phone className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-900">
                          <span className="text-gray-500 font-medium">Telefon:</span>{" "}
                          <a
                            href={`tel:${formatPhoneNumber(student.metadata.parentPhone)}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                            onClick={(e) => e.stopPropagation()}>
                            {formatPhoneNumber(student.metadata.parentPhone)}
                          </a>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <Phone className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-500">
                          <span className="font-medium">Telefon:</span> Belirtilmemiş
                        </div>
                      </div>
                    )}
                    {student.metadata?.parentEmail && (
                      <div className="flex items-center justify-center">
                        <UserCheck className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-900">
                          <span className="text-gray-500 font-medium">E-posta:</span>{" "}
                          <a
                            href={`mailto:${student.metadata.parentEmail}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline break-all"
                            onClick={(e) => e.stopPropagation()}>
                            {student.metadata.parentEmail}
                          </a>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-gray-500 italic">Veli Bilgisi Bulunmuyor</div>
                )}
              </div>

              <div className="border-t border-gray-200"></div>

              {/* Sipariş Bilgileri */}
              <div className="text-center space-y-2">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Sipariş Bilgileri</h4>
                {hasOrderInfo ? (
                  <>
                    {student.metadata?.orderCode ? (
                      <div className="flex items-center justify-center">
                        <ShoppingCart className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-900 break-all">
                          <span className="text-gray-500 font-medium">Kod:</span> {student.metadata.orderCode}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <ShoppingCart className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-500">
                          <span className="font-medium">Kod:</span> Belirtilmemiş
                        </div>
                      </div>
                    )}
                    {student.metadata?.orderDate ? (
                      <div className="flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-900">
                          <span className="text-gray-500 font-medium">Tarih:</span>{" "}
                          {new Date(student.metadata.orderDate).toLocaleDateString("tr-TR")}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <div className="text-sm text-gray-500">
                          <span className="font-medium">Tarih:</span> Belirtilmemiş
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-sm text-gray-500 italic">Sipariş Bilgisi Bulunmuyor</div>
                )}
              </div>
            </div>
          </div>
        )}

        {summary && (
          <>
            <div
              className={`bg-white rounded-lg border border-gray-200 p-3 sm:p-4 h-full ${
                hasParentOrOrderInfo ? "md:col-span-2 lg:col-span-1" : "md:col-span-1"
              }`}>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Genel İstatistikler
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
                <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                  <div className="flex items-center justify-center mb-1 sm:mb-2">
                    <ClipboardCheck className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-1" />
                    <div className="text-lg sm:text-xl font-bold text-blue-600">{summary.totalTests}</div>
                  </div>
                  <div className="text-xs text-gray-500 text-center leading-tight">Bağımsız Toplam Test Sayısı</div>
                </div>
                {reportType === "all" && summary.homeworkTests !== undefined && summary.independentTests !== undefined ? (
                  <>
                    <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                      <div className="flex items-center justify-center mb-1 sm:mb-2">
                        <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 mr-1" />
                        <div className="text-lg sm:text-xl font-bold text-orange-600">{summary.homeworkTests}</div>
                      </div>
                      <div className="text-xs text-gray-500 text-center leading-tight">Verilen Ödev Test Sayısı</div>
                    </div>
                    <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                      <div className="flex items-center justify-center mb-1 sm:mb-2">
                        <Target className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 mr-1" />
                        <div className="text-lg sm:text-xl font-bold text-indigo-600">{summary.independentTests}</div>
                      </div>
                      <div className="text-xs text-gray-500 text-center leading-tight">Çözülen Bağımsız Test Sayısı</div>
                    </div>
                    <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                      <div className="flex items-center justify-center mb-1 sm:mb-2">
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-1" />
                        <div className="text-lg sm:text-xl font-bold text-green-600">{summary.totalCompleted}</div>
                      </div>
                      <div className="text-xs text-gray-500 text-center leading-tight">Başarılı Bağımsız Test Sayısı</div>
                      <div className="text-xs text-gray-400">(%70+)</div>
                    </div>
                    <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                      <div className="flex items-center justify-center mb-1 sm:mb-2">
                        <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 mr-1" />
                        <div className="text-lg sm:text-xl font-bold text-yellow-600">{summary.totalPending}</div>
                      </div>
                      <div className="text-xs text-gray-500 text-center leading-tight">Bekleyen Bağımsız Test Sayısı</div>
                    </div>
                    {summary.totalFailed !== undefined && (
                      <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                        <div className="flex items-center justify-center mb-1 sm:mb-2">
                          <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-1" />
                          <div className="text-lg sm:text-xl font-bold text-red-600">{summary.totalFailed}</div>
                        </div>
                        <div className="text-xs text-gray-500 text-center leading-tight">Başarısız Bağımsız Test Sayısı</div>
                      </div>
                    )}
                    <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                      <div className="flex items-center justify-center mb-1 sm:mb-2">
                        <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 mr-1" />
                        <div className="text-lg sm:text-xl font-bold text-purple-600">{summary.totalQuestions}</div>
                      </div>
                      <div className="text-xs text-gray-500 text-center leading-tight">Çözülen Toplam Soru Sayısı</div>
                    </div>
                    <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                      <div className="flex items-center justify-center mb-1 sm:mb-2">
                        <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 mr-1" />
                        <div className="text-lg sm:text-xl font-bold text-emerald-600">{summary.totalCorrect}</div>
                      </div>
                      <div className="text-xs text-gray-500 text-center leading-tight">Toplam Doğru Cevap Sayısı</div>
                    </div>
                    {summary.expiredHomeworkTests !== undefined && (
                      <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                        <div className="flex items-center justify-center mb-1 sm:mb-2">
                          <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 mr-1" />
                          <div className="text-lg sm:text-xl font-bold text-orange-600">{summary.expiredHomeworkTests}</div>
                        </div>
                        <div className="text-xs text-gray-500 text-center leading-tight">Süresi Geçen Ödev Test Sayısı</div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                      <div className="flex items-center justify-center mb-1 sm:mb-2">
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-1" />
                        <div className="text-lg sm:text-xl font-bold text-green-600">{summary.totalCompleted}</div>
                      </div>
                      <div className="text-xs text-gray-500 text-center leading-tight">Başarılı Test Sayısı</div>
                      <div className="text-xs text-gray-400">(%70+)</div>
                    </div>
                    <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                      <div className="flex items-center justify-center mb-1 sm:mb-2">
                        <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 mr-1" />
                        <div className="text-lg sm:text-xl font-bold text-yellow-600">{summary.totalPending}</div>
                      </div>
                      <div className="text-xs text-gray-500 text-center leading-tight">Bekleyen Test Sayısı</div>
                    </div>
                    {summary.totalFailed !== undefined && (
                      <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                        <div className="flex items-center justify-center mb-1 sm:mb-2">
                          <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-1" />
                          <div className="text-lg sm:text-xl font-bold text-red-600">{summary.totalFailed}</div>
                        </div>
                        <div className="text-xs text-gray-500 text-center leading-tight">Başarısız Test Sayısı</div>
                      </div>
                    )}
                    <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                      <div className="flex items-center justify-center mb-1 sm:mb-2">
                        <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 mr-1" />
                        <div className="text-lg sm:text-xl font-bold text-purple-600">{summary.totalQuestions}</div>
                      </div>
                      <div className="text-xs text-gray-500 text-center leading-tight">Çözülen Toplam Soru Sayısı</div>
                    </div>
                    <div className="flex flex-col items-center justify-center py-2 sm:py-3 min-h-[60px] sm:min-h-[80px]">
                      <div className="flex items-center justify-center mb-1 sm:mb-2">
                        <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 mr-1" />
                        <div className="text-lg sm:text-xl font-bold text-emerald-600">{summary.totalCorrect}</div>
                      </div>
                      <div className="text-xs text-gray-500 text-center leading-tight">Toplam Doğru Cevap Sayısı</div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4 h-full">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
                <Trophy className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Genel Performans
              </h3>
              <div className="text-center">
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full ${overallPerformance.bgColor} mb-3 sm:mb-4`}>
                  <OverallIcon className={`w-6 h-6 sm:w-8 sm:h-8 ${overallPerformance.color}`} />
                </div>
                <div className={`text-2xl sm:text-3xl font-bold ${overallPerformance.color} mb-1 sm:mb-2`}>
                  {summary.overallSuccessPercent}%
                </div>
                <div className={`text-base sm:text-lg font-medium ${overallPerformance.color} mb-1 sm:mb-2`}>{overallPerformance.text}</div>
                <div className="text-xs sm:text-sm text-gray-500">
                  {summary.totalCorrect} / {summary.totalQuestions} Doğru Cevap
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {practiceExamsSection}

      {lessonStatistics.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center">
            <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Ders Bazında İstatistikler
          </h3>
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left sm:text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ders
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="hidden sm:inline">
                      Girdiği
                      <br />
                      Test
                      <br />
                      Sayısı
                    </span>
                    <span className="sm:hidden">Test</span>
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="hidden sm:inline">
                      Başarılı Test
                      <br />
                      Sayısı (%70+)
                    </span>
                    <span className="sm:hidden">Başarılı</span>
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Yaptığı Toplam
                    <br />
                    Soru Sayısı
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Doğru
                    <br />
                    Cevap
                    <br />
                    Sayısı
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="hidden sm:inline">
                      Genel
                      <br />
                      Başarı
                      <br />
                      Oranı
                    </span>
                    <span className="sm:hidden">Başarı</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {lessonStatistics.map((lesson) => {
                  const performance = getPerformanceLevel(lesson.successPercent);
                  const PerformanceIcon = performance.icon;
                  return (
                    <tr key={lesson.lessonId}>
                      <td className="px-2 sm:px-4 py-3 sm:py-4">
                        <div
                          className="text-xs sm:text-sm font-medium text-gray-900 truncate max-w-[120px] sm:max-w-none"
                          title={lesson.lessonTitle}>
                          {lesson.lessonTitle}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 text-center">
                        <div className="text-xs sm:text-sm text-gray-900">{lesson.totalTests}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 text-center">
                        <div className="text-xs sm:text-sm text-gray-900">{lesson.totalCompleted}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 text-center hidden md:table-cell">
                        <div className="text-xs sm:text-sm text-gray-900">{lesson.totalQuestions}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 text-center hidden lg:table-cell">
                        <div className="text-xs sm:text-sm text-gray-900">{lesson.totalCorrect}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 text-center">
                        <div className="flex items-center justify-center">
                          <PerformanceIcon className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${performance.color}`} />
                          <span className={`text-xs sm:text-sm font-medium ${performance.color}`}>{lesson.successPercent}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {topicStatistics.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 gap-3 sm:gap-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Konu Bazında İstatistikler
            </h3>
            {!selectedMainLessonFilter && (
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <label className="text-xs sm:text-sm font-medium text-gray-700">Ders Filtresi:</label>
                <select
                  value={selectedLessonFilter || ""}
                  onChange={(e) => setSelectedLessonFilter(e.target.value || null)}
                  className="border border-gray-300 rounded-md px-2 sm:px-3 py-1 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full sm:w-auto">
                  <option value="">Tüm Dersler</option>
                  {availableLessons.map((lesson) => (
                    <option key={lesson.id} value={lesson.id}>
                      {lesson.title}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left sm:text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Konu
                  </th>
                  {!selectedLessonFilter && !selectedMainLessonFilter && (
                    <th className="px-2 sm:px-4 py-2 sm:py-3 text-left sm:text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      Ders
                    </th>
                  )}
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="hidden sm:inline">
                      Girdiği
                      <br />
                      Test
                      <br />
                    </span>
                    <span className="sm:hidden">Test</span>
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="hidden sm:inline">
                      Başarılı Test
                      <br />
                      Sayısı (%70+)
                    </span>
                    <span className="sm:hidden">Başarılı</span>
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Yaptığı Toplam
                    <br />
                    Soru Sayısı
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Doğru
                    <br />
                    Cevap
                    <br />
                    Sayısı
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="hidden sm:inline">
                      Genel
                      <br />
                      Başarı
                      <br />
                    </span>
                    <span className="sm:hidden">Başarı</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTopicStatistics.map((topic) => {
                  const performance = getPerformanceLevel(topic.successPercent);
                  const PerformanceIcon = performance.icon;
                  return (
                    <tr key={topic.topicId}>
                      <td className="px-2 sm:px-4 py-3 sm:py-4">
                        <div
                          className="text-xs sm:text-sm font-medium text-gray-900 truncate max-w-[120px] sm:max-w-none"
                          title={topic.topicTitle}>
                          {topic.topicTitle}
                        </div>
                      </td>
                      {!selectedLessonFilter && !selectedMainLessonFilter && (
                        <td className="px-2 sm:px-4 py-3 sm:py-4 hidden md:table-cell">
                          <div
                            className="text-xs sm:text-sm text-gray-500 truncate max-w-[100px] sm:max-w-none"
                            title={topic.lessonTitle || "-"}>
                            {topic.lessonTitle || "-"}
                          </div>
                        </td>
                      )}
                      <td className="px-2 sm:px-4 py-3 sm:py-4 text-center">
                        <div className="text-xs sm:text-sm text-gray-900">{topic.totalTests}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 text-center">
                        <div className="text-xs sm:text-sm text-gray-900">{topic.totalCompleted}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 text-center hidden md:table-cell">
                        <div className="text-xs sm:text-sm text-gray-900">{topic.totalQuestions}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 text-center hidden lg:table-cell">
                        <div className="text-xs sm:text-sm text-gray-900">{topic.totalCorrect}</div>
                      </td>
                      <td className="px-2 sm:px-4 py-3 sm:py-4 text-center">
                        <div className="flex items-center justify-center">
                          <PerformanceIcon className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${performance.color}`} />
                          <span className={`text-xs sm:text-sm font-medium ${performance.color}`}>{topic.successPercent}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 gap-3 sm:gap-0">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Test Detayları
            {testsData?.tests?.meta && ` (${testsData.tests.meta.totalItems})`}
          </h3>
          <div className="flex items-center gap-2">
            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [newSortBy, newSortOrder] = e.target.value.split("-");
                setSortBy(newSortBy);
                setSortOrder(newSortOrder);
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="createdAt-DESC">Yeni → Eski</option>
              <option value="createdAt-ASC">Eski → Yeni</option>
              <option value="stats.percent-DESC">Başarı Oranı ↓</option>
              <option value="stats.percent-ASC">Başarı Oranı ↑</option>
            </select>
            {testsData?.tests?.meta && testsData.tests.meta.totalPages > 1 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={!testsData.tests.meta.hasPrevPage}
                  className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
                  <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
                <span className="text-xs sm:text-sm text-gray-600">
                  {testsData.tests.meta.page} / {testsData.tests.meta.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(Math.min(testsData.tests.meta.totalPages, currentPage + 1))}
                  disabled={!testsData.tests.meta.hasNextPage}
                  className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {testsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Test verileri yükleniyor...</span>
          </div>
        ) : !testsData?.tests?.data || testsData.tests.data.length === 0 ? (
          <div className="text-center py-6 sm:py-8 text-gray-500 text-sm">Bu öğrenci için test verisi bulunamadı.</div>
        ) : (
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-3 py-2 sm:py-3 text-left sm:text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Test
                  </th>
                  <th className="px-2 sm:px-3 py-2 sm:py-3 text-left sm:text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Ders/Konu
                  </th>
                  <th className="px-2 sm:px-3 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="hidden sm:inline">
                      Sonuç
                      <br />
                      (D/Y/B)
                    </span>
                    <span className="sm:hidden">Sonuç</span>
                  </th>
                  <th className="px-2 sm:px-3 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="hidden sm:inline">
                      Başarı
                      <br />
                      Oranı
                    </span>
                    <span className="sm:hidden">Başarı</span>
                  </th>
                  <th className="px-2 sm:px-3 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Durum
                  </th>
                  <th className="px-2 sm:px-3 py-2 sm:py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                    Tamamlanma
                    <br />
                    Tarihi
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {testsData.tests.data.map((test: StudentTest, index: number) => {
                  const performance = getPerformanceLevel(test.stats.percent);
                  const PerformanceIcon = performance.icon;
                  return (
                    <tr key={`${test.testId}-${test.createdAt}-${index}`}>
                      <td className="px-2 sm:px-3 py-3 sm:py-4">
                        <div
                          className="text-xs sm:text-sm font-medium text-gray-900 max-w-[120px] sm:max-w-xs truncate"
                          title={test.testTitle}>
                          {test.testTitle}
                        </div>
                        <div className="md:hidden mt-1">
                          <div className="text-xs text-gray-500 font-medium">{test.lesson?.title || "-"}</div>
                          {test.topic?.title && (
                            <div className="text-xs text-gray-400 truncate max-w-[120px]" title={test.topic.title}>
                              {test.topic.title}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-3 sm:py-4 hidden md:table-cell">
                        <div className="text-xs sm:text-sm text-gray-900">
                          <div className="font-medium">{test.lesson?.title || "-"}</div>
                          {test.topic?.title && (
                            <div className="text-xs text-gray-500 truncate max-w-xs" title={test.topic.title}>
                              {test.topic.title}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-3 sm:py-4">
                        <div className="text-xs sm:text-sm text-center">
                          <span className="text-green-600 font-semibold">{test.stats.correct}</span>
                          <span className="text-gray-400 mx-1">/</span>
                          <span className="text-red-600 font-semibold">{test.stats.wrong}</span>
                          <span className="text-gray-400 mx-1">/</span>
                          <span className="text-gray-500 font-semibold">{test.stats.empty}</span>
                          <div className="text-xs text-gray-500">({test.stats.total} soru)</div>
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-3 sm:py-4 text-center">
                        <div className="flex items-center justify-center">
                          <PerformanceIcon className={`w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 ${performance.color}`} />
                          <span className={`text-xs sm:text-sm font-medium ${performance.color}`}>{test.stats.percent}%</span>
                        </div>
                        <div className="lg:hidden mt-1">{getStatusBadge(test.status)}</div>
                      </td>
                      <td className="px-2 sm:px-3 py-3 sm:py-4 text-center hidden lg:table-cell">{getStatusBadge(test.status)}</td>
                      <td className="px-2 sm:px-3 py-3 sm:py-4 text-center hidden xl:table-cell">
                        {test.solvedAt ? (
                          <div className="flex items-center justify-center">
                            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 mr-1 sm:mr-2 flex-shrink-0" />
                            <span className="text-xs sm:text-sm text-gray-900">{formatDate(test.solvedAt)}</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-center">
                            <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mr-1 sm:mr-2 flex-shrink-0" />
                            <span className="text-xs sm:text-sm text-gray-500">Henüz Tamamlanmadı</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={isImageModalOpen}
        onClose={() => {
          setIsImageModalOpen(false);
          setSelectedImage(null);
        }}
        title={selectedImage?.name}
        size="lg">
        {selectedImage && (
          <div className="flex items-center justify-center">
            <img src={selectedImage.url} alt={selectedImage.name} className="max-w-full max-h-[70vh] object-contain rounded-lg" />
          </div>
        )}
      </Modal>
    </div>
  );
}
